/**
 * The Lucide icons this portal uses, inlined.
 *
 * The design replaces the emoji placeholders (📋 🔔 📭 🔍) with Lucide outline
 * icons. They are hand-inlined rather than pulled from `lucide-react` because
 * seven icons do not justify a dependency, and ADR-0010's ≤150 KB initial-route
 * budget is already over — see ADR-0013's consequences.
 *
 * All are 24×24, stroke-width 2, round caps and joins, `currentColor` — so they
 * inherit colour from the element that renders them and need no per-use styling.
 *
 * Source: lucide.dev, ISC licence.
 */

interface IconProps {
  /** Pixel size. Defaults to 24 to match Lucide's own viewBox. */
  size?: number;
  className?: string;
}

function Svg({
  size = 24,
  className,
  children,
}: IconProps & { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

/** lucide: eye — password visible */
export function IconEye(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

/** lucide: eye-off — password hidden */
export function IconEyeOff(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
      <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
      <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
      <path d="m2 2 20 20" />
    </Svg>
  );
}

/** lucide: clipboard-list — ordens de serviço */
export function IconOrders(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </Svg>
  );
}

/** lucide: bell — notificações */
export function IconBell(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
    </Svg>
  );
}

/** lucide: lock — the activation gate */
export function IconLock(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Svg>
  );
}

/** lucide: search — order detail placeholder */
export function IconSearch(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="m21 21-4.34-4.34" />
      <circle cx="11" cy="11" r="8" />
    </Svg>
  );
}

/** lucide: log-out — sair */
export function IconLogOut(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    </Svg>
  );
}

/** lucide: circle-alert — validation failure and inline alerts */
export function IconAlert(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </Svg>
  );
}

/** lucide: circle — a password rule not yet satisfied */
export function IconCircle(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="10" />
    </Svg>
  );
}

/** lucide: circle-check — a password rule satisfied */
export function IconCheck(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}
